import { Plus1Icon } from 'canoe';
import React from 'react';
import { Tooltip } from 'styleguide';

import { TextButton, ValueBox, ValueGroup, ValueToggle } from '@candu-ai/craft/ui';
import { Flags, useFlags } from 'commons/hooks';
import { Section } from 'craft/containers/Toolbox/components';
import { GridColumn } from 'craft/containers/Toolbox/sections/Size/GridColumn';
import { useMultiNodes } from 'craft/hooks';
import { useChildNodes } from 'craft/hooks/useChildNodes';
import { useColumnOperations } from 'craft/hooks/useColumnOperations';
import { useNodesData } from 'craft/hooks/useNodesData';
import { ColumnGapStepper } from 'craft/resolvers/Columns/toolbox/ColumnGapStepper';

import { ColumnValueRow } from './ColumnValueRow';

interface Props {
  nodeIds: string[];
  selectedIds?: string[];
}

export const ColumnsSettings = ({ nodeIds, selectedIds = [] }: Props) => {
  const { [Flags.COLUMNS_BREAKPOINTS]: withColumnsBreakpoints } = useFlags();
  const {
    type,
    css = {},
    actions: { setCss },
  } = useMultiNodes(nodeIds, (node) => ({ css: node?.data.custom.css ?? {} }));

  const columnNodeIds = useChildNodes(nodeIds[0]);
  const nodesData = useNodesData(columnNodeIds);

  const { newColumn, spaceEvenly } = useColumnOperations({ parentNodeId: nodeIds[0] });

  if (type !== 'Columns') {
    return null;
  }

  if (nodeIds.length !== 1) {
    return <></>;
  }

  const canAddColumn = columnNodeIds.length < 12 && newColumn;

  const { flexGrow, columnGap } = css;
  const isGrowActive = !!flexGrow;

  // @ts-ignore
  const total = nodesData.reduce((acc, nodeData) => acc + parseInt(nodeData.props.width, 10), 0);

  return (
    <>
      <Section name="Columns" data-cy="toolbox-section-columns">
        <ValueGroup>
          {columnNodeIds.map((id, i) => (
            <ColumnValueRow
              key={id}
              nodeId={id}
              index={i}
              total={total}
              selected={selectedIds.includes(id)}
            />
          ))}
          {canAddColumn && (
            <ValueBox label="Add New Column" isInGroup={{ isLast: true }}>
              <Tooltip content="Add" position="top" showDelay={500}>
                <ValueToggle
                  key="column-add"
                  toggleGroup="column-add"
                  id="column-add"
                  onClick={() => newColumn()}
                  isActive={false}
                  icon={Plus1Icon}
                />
              </Tooltip>
            </ValueBox>
          )}
        </ValueGroup>
        <TextButton onClick={() => spaceEvenly()}>Space Evenly</TextButton>
      </Section>
      <Section name="Column Group Settings" data-cy="toolbox-section-columns-group">
        <ValueGroup>
          <ColumnGapStepper columnGap={columnGap} setCss={setCss} />
        </ValueGroup>
        <ValueGroup>
          <ValueBox label="Grow if possible">
            <Tooltip content="flex-grow" position="top" showDelay={500}>
              <ValueToggle
                key="column-group-flex-grow"
                toggleGroup="flex-grow"
                id="column-group-toggle-flex-grow"
                onClick={() =>
                  setCss({
                    flexGrow: flexGrow !== 1 ? 1 : 0,
                  })
                }
                isActive={isGrowActive}
              />
            </Tooltip>
          </ValueBox>
        </ValueGroup>
        {withColumnsBreakpoints ? (
          <ValueGroup>
            <GridColumn nodeIds={nodeIds} />
          </ValueGroup>
        ) : null}
      </Section>
    </>
  );
};
